/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
// import { stringify } from 'query-string';
import React, { useEffect, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import useDebounce from '../../hooks/useDebounce';

import { useSearch } from '../../contexts/SearchContext';
import Button from '../buttons/Button';
import PillButton from '../buttons/Pill';
import CloseIcon from '../icons/Close';
import SearchIcon from '../icons/Search';
import RecentSearches from '../partials/RecentSearches';

import styles from '../../../styles/forms/search-bar.module.scss';

const propTypes = {
    onChange: PropTypes.func,
    onSubmit: PropTypes.func,
    value: PropTypes.string,
    withRecentSearches: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    onChange: null,
    onSubmit: null,
    value: '',
    withRecentSearches: false,
    className: null,
};

const SearchBar = ({ onChange, onSubmit, value, withRecentSearches, className }) => {
    const [internalValue, setInternalValue] = useState(
        value !== null ? decodeURIComponent(value) : '',
    );
    useEffect(() => {
        setInternalValue(value !== null ? decodeURIComponent(value) : '');
    }, [value, setInternalValue]);
    const currentValue = internalValue || '';

    const intl = useIntl();
    const [showClearSearch, setShowClearSearch] = useState(false);
    const { addRecentSearch } = useSearch();

    const onValueChange = useCallback(
        (newValue = null) => {
            if (onChange !== null) {
                onChange(encodeURIComponent(newValue));
            }
            setInternalValue(newValue);
        },
        [onChange, setInternalValue],
    );

    const onClickClearSearch = useCallback(() => {
        if (onChange !== null) onValueChange('');
        if (onSubmit !== null) onSubmit('');
    }, [onValueChange, onSubmit]);

    const onClickRecentSearch = useCallback(
        (recentSearch) => {
            if (onChange !== null) onValueChange(recentSearch);
            if (onSubmit !== null) onSubmit(recentSearch);
        },
        [onChange, onSubmit],
    );

    const onSearchChange = useCallback(
        (e) => {
            e.preventDefault();
            const newValue = e?.target?.value;
            onValueChange(newValue);
        },
        [onValueChange],
    );

    useEffect(() => {
        setShowClearSearch(value);
    }, [value, setShowClearSearch]);

    useDebounce(addRecentSearch, value, 2000);

    const onKeyDown = useCallback(
        (e) => {
            if (e.keyCode === 13) {
                document.activeElement.blur();
            }
        },
        [onChange, onSubmit],
    );

    const onBlur = useCallback(
        (e) => {
            if (currentValue && onSubmit !== null) {
                onSubmit(e);
            }
        },
        [currentValue],
    );

    return (
        <form
            onSubmit={onSubmit !== null ? onSubmit : null}
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.inner}>
                <Button className={styles.submitButton} type="submit">
                    <SearchIcon />
                </Button>
                <input
                    className={styles.input}
                    type="text"
                    placeholder={intl.formatMessage({
                        defaultMessage: 'Search',
                        description: 'Input placeholder',
                    })}
                    onChange={onSearchChange}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    value={currentValue}
                />
                {showClearSearch ? (
                    <PillButton
                        className={styles.searchClearBtn}
                        onClick={onClickClearSearch}
                        icon={<CloseIcon />}
                        type="button"
                        title={intl.formatMessage({
                            defaultMessage: 'Clear search',
                            description: 'Button label',
                        })}
                        isDark
                    />
                ) : null}
            </div>
            {withRecentSearches ? (
                <RecentSearches
                    className={styles.pastSearches}
                    onClickItem={onClickRecentSearch}
                    isDark
                />
            ) : null}
        </form>
    );
};

SearchBar.propTypes = propTypes;
SearchBar.defaultProps = defaultProps;

export default SearchBar;
